import { useQuery } from "@tanstack/react-query";

import { instance } from "@/lib/axios/instance";

// Types for LastImport Overview (client names, module status, last import dates)
export type ClientReportingLastImportOverview = {
  clientId: string;
  clientName: string;
  moduleActive: boolean;
  lastImportDate: string | null;
};

export type ReportingLastImportResponse = {
  data: ClientReportingLastImportOverview[];
  page: number;
  pageSize: number;
  totalRecords: number;
};

export type ReportingLastImportParams = {
  integrationName: string;
  tenantId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  moduleStatus?: boolean;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
};

// Query key factory
export const reportingLastImportKeys = {
  all: ["reportingLastImport"] as const,
  list: (params: ReportingLastImportParams) =>
    [...reportingLastImportKeys.all, params] as const,
};

/**
 * Hook to fetch the client overview with names, module status, and last import dates.
 */
export function useReportingLastImportOverview(params: ReportingLastImportParams) {
  const { integrationName, tenantId, page = 1, pageSize = 50, search, moduleStatus, sortColumn, sortDirection } = params;

  return useQuery({
    queryKey: reportingLastImportKeys.list(params),
    queryFn: () =>
      instance
        .get<ReportingLastImportResponse>(
          `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Reporting/last-import`,
          {
            params: {
              tenantId,
              page,
              pageSize,
              search: search || undefined,
              moduleStatus,
              sortColumn: sortColumn || undefined,
              sortDirection: sortDirection || undefined,
            },
          }
        )
        .then((res) => res.data),
    enabled: !!integrationName && !!tenantId,
    staleTime: 10 * 60 * 1000, // 10 minutes - matches backend cache
  });
}
